import { MouseEventHandler } from 'react';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

type ButtonProps = {
   children?: React.ReactNode;
   className?: string;
   color?: 'primary' | 'danger';
   disabled?: boolean;
   form?: string;
   org?: boolean;
   outline?: boolean;
   pill?: boolean;
   theme?: string;
   type?: 'submit' | 'reset' | 'button';
   onClick?: MouseEventHandler<HTMLButtonElement>;
   style?: React.CSSProperties;
   title?: string;
   /** Focusable but inactive (the click handler must ignore it): lets a screen reader reach the button and hear why. */
   'aria-disabled'?: boolean;
   /** Id of the text that explains the button, e.g. why it is not available yet. */
   'aria-describedby'?: string;
   /** Accessible name when the visible label is shared by several buttons, e.g. one per row. */
   'aria-label'?: string;
   /** The button's request is in flight. */
   'aria-busy'?: boolean;
};

const Button = ({
   children,
   className,
   color = 'primary',
   disabled,
   form,
   outline,
   pill,
   onClick,
   org,
   type = 'button',
   style,
   title,
   'aria-disabled': ariaDisabled,
   'aria-describedby': ariaDescribedBy,
   'aria-label': ariaLabel,
   'aria-busy': ariaBusy,
}: ButtonProps) => {
   const cssBase = `whitespace-nowrap flex items-center justify-center h-7.5 px-3.75 border`;
   const cssOutline = outline ? ` border-white` : color === 'danger' ? ` border-twRed bg-twRed` : ` border-primary bg-primary`;
   const cssPill = pill ? ` rounded-full` : ` rounded-md`;
   const cssDisabled = ` disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed`;
   const cssHover = color === 'danger' ? ` hover:bg-red-700 hover:border-red-700` : ` hover:bg-secondary hover:border-secondary`;
   const cssText = ` text-l-sm text-white font-semibold`;
   const css = twMerge(cssBase, cssOutline, cssPill, cssHover, cssDisabled, cssText, className || '');

   const cssBaseOrg = `whitespace-nowrap flex items-center justify-center h-7.5 px-3.75 border border-orgPrimary`;
   const cssOutlineOrg = outline ? `` : color === 'danger' ? ` border-twRed bg-twRed` : ` bg-orgPrimary`;
   const cssHoverOrg = ` hover:bg-orgSecondary hover:border-orgSecondary hover:!text-white`;
   const cssTextOrg = ` text-l-sm ${outline ? 'text-orgPrimary' : 'text-white'} font-semibold`;
   const cssOrg = twMerge(cssBaseOrg, cssOutlineOrg, cssPill, cssHoverOrg, cssDisabled, cssTextOrg, className || '');

   return (
      <motion.button
         className={org ? cssOrg : css}
         disabled={!!disabled}
         aria-disabled={ariaDisabled || undefined}
         aria-describedby={ariaDescribedBy}
         aria-label={ariaLabel}
         aria-busy={ariaBusy || undefined}
         onClick={onClick as any}
         form={form}
         title={title}
         type={type}
         style={style}
         whileHover={{
            scale: disabled || ariaDisabled ? 1 : 1.02,
            transition: { duration: 0.2 },
         }}
         whileTap={{
            scale: disabled || ariaDisabled ? 1 : 0.98,
            transition: { duration: 0.1 },
         }}
         initial={{ opacity: 0, y: 10 }}
         animate={{ opacity: 1, y: 0 }}
         transition={{ duration: 0.3 }}
      >
         {children || 'Button'}
      </motion.button>
   );
};

export default Button;
